import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { IconSize } from '@psei/ng/api';
import { merge } from 'lodash';
import { ButtonFlavor, ButtonType } from 'projects/ppb-shared-library/src/lib/models/button-config.model';
import { CustomValidators } from 'projects/ppb-shared-library/src/lib/services/form/custom-validators';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ServerConfigModel } from '../../technical/version/server-config.model';
import { AuthTokenService } from '../shared/services/auth-token.service';
import { UserService } from '../shared/services/user.service';

@Component({
  selector: 'ppb-login',
  templateUrl: './ppb-login.component.html',
  styleUrls: ['./ppb-login.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PpbLoginComponent implements OnInit, OnDestroy {
  static readonly DEFAULT_RULES_URL = 'https://www.pse.pl/portal-partnera-biznesowego/regulamin';

  loginForm: UntypedFormGroup;
  step: number = 1;
  iconSize = IconSize.Large;
  actionParam: string = null;
  isLoading$ = new BehaviorSubject(false);
  ButtonType = ButtonType;
  ButtonFlavor = ButtonFlavor;
  configuration: ServerConfigModel;

  private uuid = '';
  private subs = new Subscription();

  constructor(
    private fb: UntypedFormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private userService: UserService,
    private authTokenService: AuthTokenService,
    private cd: ChangeDetectorRef,
  ) {
    const navigation = this.router.getCurrentNavigation();
    this.actionParam = navigation ? (navigation.extras.state && navigation.extras.state.actionParam) : null;
    this.loginForm = this.fb.group({
      usernameControl: new UntypedFormControl(this.email, { validators: [Validators.required, CustomValidators.email()] }),
      passwordControl: new UntypedFormControl('', { validators: [Validators.required] }),
      smsCodeControl: new UntypedFormControl('', { validators: [Validators.required] })
    });
  }

  ngOnInit(): void {
    if (this.authTokenService.checkAuth()) {
      // noinspection JSIgnoredPromiseFromCall
      this.router.navigateByUrl('/dashboard');
    }

    this.configuration = this.determineConfiguration();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  get email(): string {
    return this.router.getCurrentNavigation().extras?.state?.email;
  }

  get emailState(): { email: string | null } {
    return {
      email: this.loginForm.get('usernameControl')?.value
    }
  }

  nextStep(step: number): void {
    const { usernameControl, passwordControl, smsCodeControl } = this.loginForm.controls;

    switch (step) {
      case 2:
        usernameControl.updateValueAndValidity();
        passwordControl.reset(null);
        passwordControl.enable();
        passwordControl.updateValueAndValidity();
        smsCodeControl.reset(null);
        smsCodeControl.updateValueAndValidity();

        if (usernameControl.valid) {
          this.step = 2;
        }
        break;
      case 3:
        passwordControl.updateValueAndValidity();
        if (passwordControl.valid) {
          this.loginStep1();
          passwordControl.disable();
        }
        break;
      case 4:
        smsCodeControl.updateValueAndValidity();
        if (smsCodeControl.valid) {
          this.loginStep2();
        }
        break;
      default:
        this.step = step;
        break;
    }
  }

  private loginStep1(): void {
    this.isLoading$.next(true);
    this.subs.add(
      this.userService.loginStep1(
        this.loginForm.getRawValue().usernameControl,
        this.loginForm.getRawValue().passwordControl)
        .subscribe(res => {
          this.uuid = res.uuid;
          this.step = 3;
          this.isLoading$.next(false);
          this.cd.markForCheck();
        }, (_err) => {
          this.isLoading$.next(false);
          this.nextStep(2);
          this.cd.markForCheck();
        })
    );
  }

  private loginStep2(): void {
    this.isLoading$.next(true);

    this.subs.add(
      this.userService.loginStep2(
        this.uuid,
        this.loginForm.getRawValue().usernameControl,
        this.loginForm.getRawValue().smsCodeControl)
        .subscribe(
          _ => this.router.navigateByUrl('/'),
          _ => this.isLoading$.next(false)
        )
    );
  }

  private determineConfiguration(): ServerConfigModel {
    let conf = this.route.snapshot.data.configuration;
    if (!conf || !conf.agreements || !conf.agreements.rulesUrl) {
      conf = merge({}, conf, { agreements: { rulesUrl: PpbLoginComponent.DEFAULT_RULES_URL } });
    }
    return conf;
  }
}
